/**
 * The banner strip: one line across the top of the screen, for the two things that are worth
 * interrupting a flight to say.
 *
 * Today those are "the network went away and the aeroplane is fine" and "a newer build is ready".
 * Both share a shape — a sentence, sometimes an action, never a dialogue box — so they share an
 * element rather than each growing their own.
 *
 * ## Why a queue and not two banners
 *
 * Because two banners stack, and a stack of banners over a cockpit is worse than the problems it
 * is reporting. Notices are keyed and ranked; the highest-ranked live notice is the one on screen,
 * and the others wait their turn silently. An update prompt is not urgent and must never cover an
 * outage message; an outage message must not be pushed off by one.
 *
 * Top-centre, which is the strip nothing else claims — the perf overlay owns top-left and the
 * flight HUD top-right — and it clears both by sitting between them.
 *
 * `updateFrame(alpha)`: none. Notices are set and cleared by events, and the element only changes
 * when the winning notice does.
 */

export interface Notice {
  /** Identity, so the same source replacing its own message does not queue a second one. */
  id: string;
  text: string;
  /** Higher wins when several are live. */
  priority: number;
  /** Optional button. Omitted for a notice that is purely informational. */
  action?: { label: string; run: () => void };
  /** `warn` for something wrong, `info` for something merely worth knowing. */
  tone?: 'info' | 'warn';
  /** Let the pilot put it away. Informational notices should; an outage should not. */
  dismissible?: boolean;
}

export class NoticeBanner {
  readonly element: HTMLDivElement;

  private readonly message: HTMLSpanElement;
  private readonly actionButton: HTMLButtonElement;
  private readonly closeButton: HTMLButtonElement;
  private readonly notices = new Map<string, Notice>();
  /** Ids the pilot has closed, so a re-published identical notice stays closed. */
  private readonly dismissed = new Set<string>();
  /** What is on screen, so an unchanged winner does not touch the DOM. */
  private showingId: string | null = null;

  constructor(parent: HTMLElement = document.body) {
    this.element = document.createElement('div');
    this.element.className = 'notice';
    this.element.hidden = true;
    // `status` rather than `alert`: an alert interrupts a screen reader mid-sentence, and none of
    // these are urgent enough to earn that.
    this.element.setAttribute('role', 'status');

    this.message = document.createElement('span');
    this.message.className = 'notice__message';

    this.actionButton = document.createElement('button');
    this.actionButton.type = 'button';
    this.actionButton.className = 'notice__action';
    this.actionButton.hidden = true;

    this.closeButton = document.createElement('button');
    this.closeButton.type = 'button';
    this.closeButton.className = 'notice__close';
    this.closeButton.textContent = '×';
    this.closeButton.setAttribute('aria-label', 'Dismiss');
    this.closeButton.hidden = true;
    this.closeButton.addEventListener('click', () => {
      if (this.showingId !== null) this.dismissed.add(this.showingId);
      this.refresh();
    });

    this.element.append(this.message, this.actionButton, this.closeButton);
    parent.append(this.element);
  }

  /** Publish or replace a notice. Identical text under the same id is a no-op. */
  show(notice: Notice): void {
    const existing = this.notices.get(notice.id);
    if (existing !== undefined && existing.text === notice.text && existing.action === undefined) {
      // Same source saying the same thing: nothing has changed, so leave the dismissal state be.
      return;
    }
    // New wording under an existing id is genuinely new information, so it earns the screen back
    // even if the previous message was dismissed.
    if (existing !== undefined && existing.text !== notice.text) this.dismissed.delete(notice.id);
    this.notices.set(notice.id, notice);
    this.refresh();
  }

  /** Withdraw a notice — the outage ended, the update was taken. */
  clear(id: string): void {
    if (!this.notices.delete(id)) return;
    this.dismissed.delete(id);
    this.refresh();
  }

  /** Which notice is on screen, or null. Exposed for the tests and for the debug handle. */
  get visibleId(): string | null {
    return this.showingId;
  }

  private refresh(): void {
    let winner: Notice | null = null;
    for (const notice of this.notices.values()) {
      if (this.dismissed.has(notice.id)) continue;
      if (winner === null || notice.priority > winner.priority) winner = notice;
    }

    if (winner === null) {
      this.showingId = null;
      this.element.hidden = true;
      return;
    }

    this.showingId = winner.id;
    this.message.textContent = winner.text;
    this.element.className = winner.tone === 'warn' ? 'notice notice--warn' : 'notice';

    const action = winner.action;
    if (action === undefined) {
      this.actionButton.hidden = true;
      this.actionButton.onclick = null;
    } else {
      this.actionButton.hidden = false;
      this.actionButton.textContent = action.label;
      // Assigned rather than added: `refresh` runs whenever anything changes, and
      // `addEventListener` would stack a fresh handler on the same button every time.
      this.actionButton.onclick = () => action.run();
    }

    this.closeButton.hidden = winner.dismissible !== true;
    this.element.hidden = false;
  }

  dispose(): void {
    this.element.remove();
  }
}
